import ListFilters from "@/common/components/ListFilters";
import ListHeader from "@/common/components/ListHeader";
import TextField from "@/common/components/TextField";
import useDebounce from "@/common/hooks/useDebounce";
import { Mapper } from "@/common/services/mapper";
import { SearchIcon } from "lucide-react";
import Table from "@/common/components/Table";
import { useProductCategories } from "@/common/hooks/queries/useProductCategories";
import { ProductCategoryData } from "@/common/types/productCategory.type";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { useTefProductCodes } from "../../hooks/useTefProductCodes";
import { TefProductCodesTransformer } from "../../services/tefProductCodesTransformer";
import TefConfigurationProductCreateModal from "../Create";
import { tefConfigurationProductsTableColumns } from "../../constants/tefConfigurationProductsTableColumns.constant";
import { tefProviders } from "../../constants/tefModels.constants";
import { NotFoundErrorMessage } from "@/common/components/NotFoundErrorMessage";
import { useParams } from "react-router-dom";

const TefConfigurationsList = () => {
  const { provider } = useParams();

  const { filterParams, setFilterParams } = useFilterParams();

  const Icon = provider ? tefProviders[provider]?.Icon : null;

  const [search, setSearch] = useDebounce(filterParams.search || '');
  const [searchProductCategories, setSearchProductCategories] = useDebounce(filterParams.search || '');

  const { data: productCategoryOptions } = useProductCategories({
    filters: { search: searchProductCategories },
    mapper: Mapper.mapToOptions<ProductCategoryData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  });

  const {
    data: tefProductCodes,
    meta: tefProductCodesMeta,
    isPending: tefProductCodesIsPending,
    error: tefProductCodesError = {} as any,
    refetch: refetchTefProductCodes,
  } = useTefProductCodes({
    filters: {
      search,
      page: Number(filterParams.page) || 1,
      provider: provider,
      productCategoryId: filterParams.product_category?.value,
    },
    mapper: Mapper.mapWithTransformer({ transformer: TefProductCodesTransformer.tableTransformer() })
  });

  if (!provider) {
    return <NotFoundErrorMessage />
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex">
        <ListHeader
          Icon={Icon ? <Icon className="w-10 h-10" strokeWidth={1.5} /> : <></>}
          title={`Tabela de códigos ${provider ? tefProviders[provider]?.label : ''}`}
        />

        <TefConfigurationProductCreateModal
          provider={provider}
        />
      </div>

      <ErrorMessageWrapper
        className="max-h-[calc(100vh-128px)]"
        statusCode={tefProductCodesError?.status}
        refetchFunction={refetchTefProductCodes}
      >
        <div className="h-[calc(100vh-128px)] flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <TextField
              LeftIcon={<SearchIcon />}
              placeholder="Pesquisar"
              className="rounded-sm shadow-xl"
              onChange={(e) => {
                setSearch((e.target as HTMLInputElement).value);
                setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value, page: null }));
              }}
              value={filterParams.search}
            />

            <div className="flex items-center justify-start gap-2">
              <ListFilters
                filters={[
                  {
                    className: 'min-w-40',
                    onChange: (product_category) => setFilterParams((prev) => ({ ...prev, product_category, page: null })),
                    onInputChange: setSearchProductCategories,
                    value: filterParams.product_category,
                    options: [{ label: 'Todas', value: '' }, ...productCategoryOptions],
                    placeholder: 'Serviços'
                  },
                ]}
              />
            </div>
          </div>

          <Table
            columns={tefConfigurationProductsTableColumns(provider)}
            isLoadingData={tefProductCodesIsPending}
            rows={tefProductCodes || []}
            totalPages={tefProductCodesMeta.total_pages}
            totalCount={tefProductCodesMeta.total}
            hasPagination
            page={Number(filterParams.page) || 1}
            setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
            rowEmptyValue="--"
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  );
};

export default TefConfigurationsList;
